import { useEffect, useMemo, useState } from "react";

import { WorkModel } from "../../factories/works/models/work.model";
import { useAppDispatch } from "../../redux/redux";
import { billingAPI, Invoice, SortEnum } from "../../services/BillingService";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { userSlice } from "../../slices/UserSlice";
import { workSlice } from "../../slices/WorkSlice";
import { WorkTableSort } from "../works/types/work-table";
import { InvoiceLike, RevenueLine, TimeEntry } from "./reports.utils";

/** Rows per request; pages are loaded one after another until all are in. */
const PAGE = 500;
/** Safety stop: never load more than this many rows for one report. */
const MAX_ROWS = 5000;

type Loaded<T> = { data: T[]; isLoading: boolean; isError: boolean };

/**
 * Loads every page of a list (in order) and returns all rows. `key` decides
 * when to reload; `fetchPage` returns one page's rows and the total count.
 */
const usePagedList = <T>(
  key: string,
  skip: boolean,
  fetchPage: (page: number) => Promise<{ rows: T[]; total: number }>,
): Loaded<T> => {
  const [state, setState] = useState<Loaded<T>>({
    data: [],
    isLoading: true,
    isError: false,
  });

  useEffect(() => {
    if (skip) return;
    let cancelled = false;
    setState((s) => ({ ...s, isLoading: true, isError: false }));
    (async () => {
      const rows: T[] = [];
      for (let page = 1; rows.length < MAX_ROWS; page += 1) {
        const result = await fetchPage(page);
        rows.push(...result.rows);
        if (!result.rows.length || rows.length >= result.total) break;
      }
      if (!cancelled)
        setState({ data: rows, isLoading: false, isError: false });
    })().catch(() => {
      if (!cancelled) setState({ data: [], isLoading: false, isError: true });
    });
    return () => {
      cancelled = true;
    };
    // fetchPage is rebuilt every render; `key` describes the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, skip]);

  return state;
};

export type WorkQuery = {
  userIds?: string[];
  customerIds?: string[];
  worktypeIds?: string[];
  workStatuses?: string[];
  /** true = only open work, false = all work. */
  hideGreenStatus: boolean;
};

/** All work items matching the query (find-work, every page). */
export const useAllWorks = (query: WorkQuery, skip = false) => {
  const dispatch = useAppDispatch();
  const orgId = getLinkedFirm()?.orgId || "";
  const key = JSON.stringify({ orgId, ...query });

  return usePagedList<WorkModel>(key, skip || !orgId, async (pageNumber) => {
    const request = dispatch(
      workSlice.endpoints.getWorks.initiate(
        {
          orgId,
          pageNumber,
          pageSize: PAGE,
          sortBy: WorkTableSort.StartDate,
          sortOrder: "desc",
          customerIds: query.customerIds ?? [],
          userIds: query.userIds ?? [],
          worktypeIds: query.worktypeIds ?? [],
          workStatuses: query.workStatuses ?? [],
          isArchived: false,
          hideGreenStatus: query.hideGreenStatus,
        },
        { forceRefetch: true },
      ),
    );
    try {
      const rows = await request.unwrap();
      return { rows, total: rows[0]?.totalRecords ?? 0 };
    } finally {
      request.unsubscribe();
    }
  });
};

export type TimeQuery = {
  from?: Date | null;
  to?: Date | null;
  userIds?: string[];
  customerIds?: string[];
  workTypeIds?: string[];
  /** false = only time not on an invoice yet. */
  billed?: boolean | null;
};

/** All time entries matching the query (filter-time, every page). */
export const useAllTime = (query: TimeQuery, skip = false) => {
  const dispatch = useAppDispatch();
  const orgId = getLinkedFirm()?.orgId || "";
  const key = JSON.stringify({ orgId, ...query });
  const hasRange = !!query.from && !!query.to;

  return usePagedList<TimeEntry>(key, skip || !orgId, async (pageNumber) => {
    const request = dispatch(
      userSlice.endpoints.filterTime.initiate(
        {
          orgId,
          filterByDate: hasRange ? ["Custom"] : [],
          fromDate: hasRange ? (query.from as Date).toISOString() : null,
          toDate: hasRange ? (query.to as Date).toISOString() : null,
          filterByAssignee: query.userIds ?? [],
          filterByClient: query.customerIds ?? [],
          filterByWorkType: query.workTypeIds ?? [],
          filterByStatus: query.billed ?? null,
          // The API fails without these two (same as the Time page sends).
          filterByWork: [],
          filterByWorkTask: [],
          sortBy: "Date",
          sortOrder: "desc",
          pageNumber,
          pageSize: PAGE,
        },
        { forceRefetch: true },
      ),
    );
    try {
      const result = await request.unwrap();
      return {
        rows: (result.items ?? []) as unknown as TimeEntry[],
        total: result.totalCount ?? 0,
      };
    } finally {
      request.unsubscribe();
    }
  });
};

export type InvoiceQuery = {
  from?: Date | null;
  to?: Date | null;
  customerIds?: string[];
  userIds?: string[];
};

/** All invoices matching the query (filter-invoice, every page). */
export const useAllInvoices = (query: InvoiceQuery, skip = false) => {
  const dispatch = useAppDispatch();
  const orgId = getLinkedFirm()?.orgId || "";
  const key = JSON.stringify({ orgId, ...query });
  const hasRange = !!query.from && !!query.to;

  return usePagedList<InvoiceLike>(key, skip || !orgId, async (pageNumber) => {
    const request = dispatch(
      billingAPI.endpoints.getInvoices.initiate(
        {
          orgId,
          filterByDate: hasRange ? ["Custom"] : [],
          fromDate: hasRange ? (query.from as Date).toISOString() : null,
          toDate: hasRange ? (query.to as Date).toISOString() : null,
          sortBy: SortEnum.InvoiceDate,
          sortOrder: "desc",
          filterByStatus: [],
          filterByClient: query.customerIds ?? [],
          filterByAssignee: query.userIds ?? [],
          filterByLabel: [],
          pageNumber,
          pageSize: PAGE,
        },
        { forceRefetch: true },
      ),
    );
    try {
      const rows = (await request.unwrap()) as Invoice[];
      return {
        rows: rows as unknown as InvoiceLike[],
        total: rows[0]?.totalRecords ?? 0,
      };
    } finally {
      request.unsubscribe();
    }
  });
};

/** Invoice lines (service + amount) of every invoice, for "Revenue by service". */
export const useInvoiceLines = (skip: boolean) => {
  const dispatch = useAppDispatch();
  const orgId = getLinkedFirm()?.orgId || "";
  const [state, setState] = useState<Loaded<RevenueLine>>({
    data: [],
    isLoading: true,
    isError: false,
  });

  useEffect(() => {
    if (skip || !orgId) return;
    let cancelled = false;
    const request = dispatch(
      billingAPI.endpoints.getAllInvoices.initiate(orgId),
    );
    request
      .unwrap()
      .then((invoices) => {
        if (cancelled) return;
        const lines = invoices.flatMap((invoice) =>
          (invoice.invoiceItem ?? []).map((item) => ({
            invoiceId: String(
              (invoice as unknown as { id?: string }).id ||
                invoice.invoiceId ||
                "",
            ),
            name: item.billingService?.name || "No service",
            amount: item.amount || 0,
          })),
        );
        setState({ data: lines, isLoading: false, isError: false });
      })
      .catch(() => {
        if (!cancelled) setState({ data: [], isLoading: false, isError: true });
      })
      .finally(() => request.unsubscribe());
    return () => {
      cancelled = true;
    };
  }, [skip, orgId, dispatch]);

  return state;
};

/** Memoised filter of `rows` (helper for reports that filter in the browser). */
export const useFiltered = <T>(
  rows: T[],
  keep: (row: T) => boolean,
  deps: unknown[],
) =>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => rows.filter(keep), [rows, ...deps]);
